'use client';

import { useEffect, useState } from 'react';

interface HealthCheckLoadingProps {
  endpoint: string;
  maxSeconds?: number;
}

/**
 * Loading component that shows a countdown timer
 * Displays how many seconds remain before timeout
 */
export function HealthCheckLoadingWithCountdown({ 
  endpoint, 
  maxSeconds = 5 
}: HealthCheckLoadingProps) {
  const [secondsRemaining, setSecondsRemaining] = useState(maxSeconds);
  
  useEffect(() => {
    const startTime = Date.now();
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, maxSeconds - elapsed);
      setSecondsRemaining(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 100); // Update more frequently for smooth countdown
    
    return () => clearInterval(interval);
  }, [maxSeconds]);
  
  return (
    <div className="rounded px-2 py-1.5 bg-gray-700/30 border border-gray-600/30 animate-pulse">
      <div className="flex items-center gap-2">
        {/* Spinning loader */}
        <div className="relative">
          <div className="h-3 w-3 border-2 border-gray-500 border-t-blue-400 rounded-full animate-spin" />
        </div>
        
        {/* Endpoint name */}
        <span className="text-gray-400 text-xs font-medium">{endpoint}</span>
        
        {/* Countdown timer */}
        <div className="ml-auto flex items-center gap-1">
          <span className="text-gray-500 text-[10px]">checking</span>
          <span className="font-mono text-blue-400 text-xs font-semibold min-w-[20px] text-center">
            {secondsRemaining}s
          </span>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-1.5 h-0.5 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-100 ease-linear"
          style={{ 
            width: `${(secondsRemaining / maxSeconds) * 100}%` 
          }}
        />
      </div>
    </div>
  );
}
